import { MVRViolations } from '../../../tasks/types/mvr';

export type DispositionCategory = 'guilty' | 'dismissed' | 'admin' | 'default';

/**
 * Maps a free-text disposition to one of the styled chip/pill categories.
 * Disposition values arrive as free strings from the provider, so we match on
 * substrings rather than exact equality.
 */
export const categorizeDisposition = (
  disposition?: string,
): DispositionCategory => {
  const value = (disposition || '').toLowerCase();
  if (value.includes('dismiss')) {
    return 'dismissed';
  }
  // Guard against negations ("not guilty", "non-guilty") before the guilt branch,
  // otherwise the substring match would mis-flag them as guilty.
  if (value.includes('not guilt') || value.includes('non-guilt')) {
    return 'default';
  }
  if (value.includes('guilt')) {
    return 'guilty';
  }
  if (value.includes('admin')) {
    return 'admin';
  }
  return 'default';
};

export interface DispositionSummary {
  label: string;
  count: number;
  category: DispositionCategory;
}

/**
 * Groups violations by their (display) disposition, preserving first-seen order,
 * and tags each group with its chip category for styling.
 */
export const summarizeDispositions = (
  violations: MVRViolations[],
): DispositionSummary[] => {
  const order: string[] = [];
  const byLabel = new Map<string, DispositionSummary>();

  violations.forEach(violation => {
    const label = violation.disposition || 'Unknown';
    const existing = byLabel.get(label);
    if (existing) {
      existing.count += 1;
    } else {
      order.push(label);
      byLabel.set(label, {
        label,
        count: 1,
        category: categorizeDisposition(violation.disposition),
      });
    }
  });

  return order.map(label => byLabel.get(label) as DispositionSummary);
};

/** Sums numeric violation points, ignoring null/undefined entries. */
export const sumPoints = (violations: MVRViolations[]): number =>
  violations.reduce(
    (total, violation) =>
      total + (typeof violation.points === 'number' ? violation.points : 0),
    0,
  );
